// src/components/Student/StudentLogin.tsx
import React, { useMemo, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import axios from "axios";
import drIsInLogo from "../../assets/dr-is-in.png";
import "./StudentLogin.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
const raw = axios.create({ baseURL: API_BASE, timeout: 15000 });

function normalizeBearer(t: string) {
  return (t || "").replace(/^Bearer\s+/i, "").trim();
}
function clean(v: string) {
  return (v || "").trim();
}
function normOrgCode(v: string) {
  return clean(v).replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}
function digitsOnly(v: string) {
  return (v || "").replace(/[^\d]/g, "");
}

function setBoth(k: string, v: string) {
  try {
    sessionStorage.setItem(k, v);
  } catch {}
  try {
    localStorage.setItem(k, v);
  } catch {}
}

const StudentLogin: React.FC = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [orgCode, setOrgCode] = useState("");
  const [studentId, setStudentId] = useState("");
  const [pin, setPin] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = useMemo(() => {
    return normOrgCode(orgCode).length > 0 && clean(studentId).length > 0 && clean(pin).length === 6;
  }, [orgCode, studentId, pin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const payload = {
        org_code: normOrgCode(orgCode),
        student_id: clean(studentId),
        pin: clean(pin),
      };

      const res = await raw.post("/auth/login_student", payload, {
        headers: { "Content-Type": "application/json" },
      });

      const tokenRaw = String(res?.data?.access_token || res?.data?.token || "");
      const role = String(res?.data?.role || "");

      if (!tokenRaw || role !== "student") {
        throw new Error("Student login failed (missing token or role).");
      }

      const token = normalizeBearer(tokenRaw);

      setBoth("role", "student");
      setBoth("student_access_token", token);
      setBoth("student_token", token);
      setBoth("access_token", token);
      setBoth("token", token);
      setBoth("org_code", String(res?.data?.org_code || payload.org_code));
      setBoth("student_id", String(res?.data?.student_id || payload.student_id));

      if (res?.data?.student_pk != null) {
        setBoth("student_pk", String(res.data.student_pk));
      }

      navigate("/student-portal", { replace: true });
    } catch (e: any) {
      const detail = e?.response?.data?.detail ?? e?.response?.data ?? e?.message;
      setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-login-page">
      <div className="student-login-card">
        <div className="student-login-brand">
          <img className="student-login-logo" src={drIsInLogo} alt="Doctor Is In" />
          <div className="student-login-brandtext">
            <div className="student-login-title">Student Login</div>
            <div className="student-login-tagline">AI Wellness Advisor with a human touch.</div>
            <div className="student-login-subtitle">Enter your Org Code, Student ID, and 6-digit PIN</div>
          </div>
        </div>

        {err ? <div className="student-login-error">{err}</div> : null}

        <form onSubmit={handleSubmit}>
          <label className="student-login-label">Org Code</label>
          <input
            className="student-login-input"
            value={orgCode}
            onChange={(e) => setOrgCode(e.target.value)}
            onBlur={() => setOrgCode(normOrgCode(orgCode))}
            placeholder="e.g. SMHSUNION"
            autoComplete="off"
          />

          <label className="student-login-label">Student ID</label>
          <input
            className="student-login-input"
            value={studentId}
            onChange={(e) => setStudentId(clean(e.target.value))}
            placeholder="e.g. 800641"
            autoComplete="off"
          />

          <label className="student-login-label">PIN</label>
          <input
            className="student-login-input"
            value={pin}
            onChange={(e) => setPin(digitsOnly(e.target.value).slice(0, 6))}
            placeholder="6-digit PIN"
            inputMode="numeric"
            autoComplete="off"
          />

          <button className="student-login-btn" type="submit" disabled={!canSubmit || loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="student-login-links">
          <NavLink to="/">← Home</NavLink>
          <NavLink to="/login">Advisor / Admin Login</NavLink>
        </div>

        <div className="student-login-footer">© {year} Doctor Is In App. All rights reserved.</div>
      </div>
    </div>
  );
};

export default StudentLogin;
